<template>
  <button
    class="button button--transparent button--no-padding button--less-margin"
    type="button"
    aria-label="Undo"
    :disabled="!canUndo"
    @click="undo"
  >
    <UndoArrow32Svg class="icon icon--large" />
  </button>
  <button
    class="button button--transparent button--no-padding button--less-margin"
    type="button"
    aria-label="Redo"
    :disabled="!canRedo"
    @click="redo"
  >
    <RedoArrow32Svg class="icon icon--large" />
  </button>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue';

import UndoArrow32Svg from '@/images/undo-arrow-32.svg';
import RedoArrow32Svg from '@/images/redo-arrow-32.svg';

const props = defineProps({
  map: { type: Object, required: true },
  getState: { type: Function, required: true },
});
const emit = defineEmits(['update']);

const undoBuffer = reactive([]);
const redoBuffer = reactive([]);
const drawingRedoBuffer = reactive([]);
const redoingDraw = ref(false);

const canUndo = ref(false);
const canRedo = ref(false);

onMounted(() => {
  props.map.on('editable:vertex:dragend', pushUndoUnlessDrawing);
  props.map.on('editable:vertex:deleted', pushUndoUnlessDrawing);
  props.map.on('editable:vertex:new', () => {
    if (props.map.isDrawing() && !redoingDraw.value) {
      drawingRedoBuffer.length = 0;
      canUndo.value = true;
      canRedo.value = false;
    }
  });
  props.map.on('editable:drawing:end', () => {
    drawingRedoBuffer.length = 0;
    canUndo.value = checkCanUndo();
  });
  props.map.on('editable:drawing:clicked', () => {
    canUndo.value = checkCanUndo();
  });
  document.addEventListener('keydown', onKeyPress, false);

  undoBuffer.push(props.getState());
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeyPress);
});

function onKeyPress(event) {
  if ((event.ctrlKey || event.metaKey) && (event.key === 'z' || event.key === 'Z')) {
    if (event.shiftKey) {
      redo();
    } else {
      undo();
    }
    event.preventDefault();
  }
}

function pushUndoUnlessDrawing() {
  if (props.map.isDrawing()) return;
  pushUndo();
}

function pushUndo() {
  let state = props.getState();

  undoBuffer.push(state);
  redoBuffer.length = 0;
  canUndo.value = true;
  canRedo.value = false;
}

function checkCanUndo() {
  if (props.map.isDrawing()) {
    let map = props.map.getMap();
    return map.editTools._drawingEditor && map.editTools._drawingEditor._drawnLatLngs.length > 0;
  }
  return undoBuffer.length > 1;
}

function checkCanRedo() {
  if (props.map.isDrawing()) {
    return drawingRedoBuffer.length > 0;
  }
  return redoBuffer.length > 0;
}

function undo() {
  if (!checkCanUndo()) return;

  if (props.map.isDrawing()) {
    let latlng = props.map.getMap().editTools._drawingEditor.pop();
    if (latlng) drawingRedoBuffer.push(latlng);
    canUndo.value = checkCanUndo();
  } else {
    redoBuffer.push(undoBuffer.pop());
    let previousState = undoBuffer[undoBuffer.length - 1];
    emit('update', previousState);
  }

  canUndo.value = checkCanUndo();
  canRedo.value = true;
}

function redo() {
  if (!checkCanRedo()) return;

  if (props.map.isDrawing()) {
    if (drawingRedoBuffer.length) {
      redoingDraw.value = true;
      props.map.getMap().editTools._drawingEditor.push(drawingRedoBuffer.pop());
      redoingDraw.value = false;
    }
  } else {
    let nextState = redoBuffer.pop();
    undoBuffer.push(nextState);

    emit('update', nextState);
  }

  canRedo.value = checkCanRedo();
  canUndo.value = true;
}

defineExpose({
  pushUndoUnlessDrawing,
  pushUndo,
  checkCanUndo,
  checkCanRedo,
  undo,
  redo
});
</script>
